import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiServer } from 'react-icons/fi';
import toast from 'react-hot-toast';

import nl from "../../src/assets/images/netherland.png"
import usa from "../../src/assets/images/usa.png"

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SERVERS_PER_PAGE = 9;

const MyServices = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/servers/my-servers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const nextServers = response.data?.servers || [];
      setServers(nextServers);
      setCurrentPage(1);
    } catch (error) {
      setServers([]);
      toast.error(error.response?.data?.message || 'Failed to load servers');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(servers.length / SERVERS_PER_PAGE);
  const paginatedServers = servers.slice(
    (currentPage - 1) * SERVERS_PER_PAGE,
    currentPage * SERVERS_PER_PAGE
  );

  const toNum = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
      const cleaned = v.replace(/,/g, '');
      const match = cleaned.match(/-?\d+(\.\d+)?/);
      if (!match) return null;
      const n = Number(match[0]);
      return Number.isFinite(n) ? n : null;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const clamp = (n, min = 0, max = 100) => Math.min(max, Math.max(min, n));

  const getCpuPercent = (server) => {
    const stats = server?.acerdpData?.stats || {};
    const candidates = [
      stats.cpu,
      stats.cpu_usage,
      stats.cpu_percent,
      stats.cpu_percentage,
      stats.cpu_usage_percentage,
      server?.acerdpData?.cpu,
      server?.acerdpData?.cpu_usage,
      server?.acerdpData?.cpu_percent,
      server?.cpu,
    ];

    for (const c of candidates) {
      const n = toNum(c);
      if (n === null) continue;
      const pct = n > 0 && n <= 1 ? n * 100 : n; // handle 0..1
      if (Number.isFinite(pct) && pct >= 0 && pct <= 100) return pct;
    }

    return null;
  };

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex min-w-0 max-w-full flex-1 flex-col space-y-6 overflow-x-hidden md:mx-7">
        <div className="flex flex-row items-center justify-between gap-3 min-w-0 animate-cardIn">
          <h1 className="min-w-0 truncate shrink text-xl sm:text-2xl md:text-4xl font-light text-white pr-2">My Servers</h1>
          <div className="flex shrink-0 items-center">
            <Link
              to="/select-plan"
              className="rounded-xl px-3 py-2 md:px-4 md:py-2 text-xs sm:text-sm text-white whitespace-nowrap
              bg-[#FFFFFF0F]
              shadow-[inset_0_1px_1px_rgba(255,255,255,0.23),0_2px_4px_rgba(0,0,0,0.25),inset_0_-16px_28px_rgba(255,255,255,0.15)]
              hover:bg-[rgba(255,255,255,0.10)]
              transition-all duration-200"
            >
              Order New Server
            </Link>
          </div>
        </div>

        {servers.length === 0 ? (
          <div className="rdp-card flex flex-1 flex-col items-center justify-center text-center animate-cardIn p-12">
            <FiServer className="text-blue-300/60 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-white mb-2">No servers yet</h3>
            <p className="text-blue-100/70 mb-6">Get started by ordering your first RDP server</p>
            <Link
              to="/select-plan"
              className="inline-block rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-500"
            >
              Order Now
            </Link>
          </div>
        ) : (
          <div className="min-w-0 overflow-hidden rounded-3xl border border-[#B8B8B847] bg-[#212A5533] p-3 font-geist space-y-5 animate-cardIn sm:p-5">
            <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {paginatedServers.map((server, idx) => {
                const cpu = getCpuPercent(server);
                const filled = cpu === null ? 0 : Math.round((cpu / 100) * 14);
                const loc = String(server?.location || '').toLowerCase();
                const isNl = loc === 'nl' || loc.includes('nether') || loc.includes('amsterdam');

                return (
                <Link
                  key={server._id}
                  to={`/server/${server._id}`}
                  className="rdp-card group block min-w-0 max-w-full overflow-hidden border border-[#EBEBEB1C] bg-[#FFFFFF14] p-4 animate-cardIn sm:p-5
                  transition-all duration-300 ease-out hover:-translate-y-1 hover:border-blue-300/45 hover:bg-[#FFFFFF18]
                  hover:shadow-[0_22px_60px_rgba(60,60,255,0.16)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F6BFF]/60"
                  style={{ animationDelay: `${idx * 90}ms` }}
                >
                  {/* Status + Days */}
                  <div className="flex items-center justify-between text-sm sm:text-md text-blue-100/70">
                    <span className="rounded-full border border-[#009439] bg-[#00943942] px-2 sm:px-2.5 py-0.5 sm:py-1 text-[#009439] text-xs sm:text-sm">
                      {server.status || 'Active'}
                    </span>
                    <span className="text-white text-xs sm:text-sm">
                      {Math.max(Math.ceil((new Date(server.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)), 0)} days left
                    </span>
                  </div>

                  {/* Server Name */}
                  <h3 className="mt-3 min-w-0 truncate text-xl font-light leading-tight text-white sm:mt-4 sm:text-3xl md:text-[45px]">
                    {server.name || '—'}
                  </h3>

                  {/* Location */}
                  <p className="mt-2 sm:mt-3 flex items-center gap-1 text-white text-sm sm:text-base">
                    <img
                      src={isNl ? nl : usa}
                      alt={isNl ? 'NL' : 'US'}
                      className="w-4 h-4 sm:w-5 sm:h-5"
                    />
                    <span className="truncate">
                      {isNl ? 'Netherlands' : 'United States'}
                    </span>
                  </p>

                  {/* Bottom Row */}
                  <div className="mt-3 flex flex-col gap-3 sm:mt-4 sm:flex-row sm:items-end sm:justify-between sm:gap-2">
                    {/* IP + CPU */}
                    <div className="flex min-w-0 flex-col items-start gap-2 sm:gap-3">
                      <p className="max-w-full truncate font-light text-xs text-white sm:text-sm">
                        {server.ip || '—'}
                      </p>
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="flex min-w-0 shrink items-end gap-[2px] sm:gap-[3px]">
                          {Array.from({ length: 14 }).map((_, i) => (
                            <div
                              key={i}
                              className={`h-4 w-[2px] shrink-0 rounded-full sm:h-6 sm:w-[3px] ${
                                i < filled ? 'bg-[#4F6BFF]' : 'bg-[#5E6170]'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="shrink-0 font-light text-xs text-white sm:text-sm">
                          {cpu === null ? '—' : `${Math.round(clamp(cpu))}%`}
                        </p>
                      </div>
                    </div>

                    {/* Renew + Manage */}
                    <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:flex-col sm:items-end">
                      <span className="shrink-0 text-xs text-blue-100/75 sm:text-sm">
                        Renew: {server.autoRenew ? 'ON' : 'OFF'}
                      </span>
                      <span className="flex w-full max-w-[140px] shrink-0 items-center justify-center rounded-2xl py-1.5 text-xs font-light text-white sm:w-[153px] sm:py-2 sm:text-sm
                        bg-[rgba(255,255,255,0.06)] 
                        shadow-[inset_0_1px_1px_rgba(255,255,255,0.23),0_2px_4px_rgba(0,0,0,0.25),inset_0_-16px_28px_rgba(255,255,255,0.15)]
                        hover:bg-[rgba(255,255,255,0.09)] transition-all duration-200 sm:max-w-none">
                        Manage
                      </span>
                    </div>
                  </div>
                </Link>
              )})}
            </div>

            {/* Pagination — shfaqet vetem nese ka me shume se 9 servers */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-2">
                {/* Prev */}
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-white
                    bg-[rgba(255,255,255,0.06)] border border-[#EBEBEB1C]
                    hover:bg-[rgba(255,255,255,0.12)] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                >
                  ‹
                </button>

                {/* Page Numbers */}
                {Array.from({ length: totalPages }).map((_, i) => {
                  const page = i + 1;
                  const isActive = page === currentPage;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-light transition-all duration-200
                        ${isActive
                          ? 'bg-[#4F6BFF] text-white shadow-[0_0_12px_rgba(79,107,255,0.4)]'
                          : 'text-white bg-[rgba(255,255,255,0.06)] border border-[#EBEBEB1C] hover:bg-[rgba(255,255,255,0.12)]'
                        }`}
                    >
                      {page}
                    </button>
                  );
                })}

                {/* Next */}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-white
                    bg-[rgba(255,255,255,0.06)] border border-[#EBEBEB1C]
                    hover:bg-[rgba(255,255,255,0.12)] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        )}
      </div>

    </Layout>
  );
};

export default MyServices;